import { useCallback, useEffect, useState } from "react";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Alert } from "@/components/ui/Alert";
import { Spinner } from "@/components/ui/Spinner";
import { TextInput } from "@/components/ui/Field";
import { ApiError } from "@/lib/apiClient";
import {
  getDatevSettings,
  runDatevBackfill,
  runSharepointBackfill,
  saveDatevSettings,
} from "@/lib/dokumente";
import { toast } from "@/lib/toast";
import { SettingsAccessDenied } from "../../SettingsLayout";
import { useSettingsAcl } from "../../lib/useSettingsAcl";

function formatDateDe(iso: string): string {
  const parsed = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return iso;
  return parsed.toLocaleDateString("de-DE");
}

export function DatevSettingsSection() {
  const acl = useSettingsAcl();
  const [email, setEmail] = useState("");
  const [savedEmail, setSavedEmail] = useState("");
  const [backfillFrom, setBackfillFrom] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [datevBackfilling, setDatevBackfilling] = useState(false);
  const [backfilling, setBackfilling] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const settings = await getDatevSettings();
      setEmail(settings.email);
      setSavedEmail(settings.email);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "DATEV-Einstellungen konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (!acl.loading && !acl.canAuftraege) {
    return <SettingsAccessDenied />;
  }

  async function handleSave() {
    setSaving(true);
    setError(null);
    try {
      const saved = await saveDatevSettings(email.trim());
      setEmail(saved.email);
      setSavedEmail(saved.email);
      toast.success(
        saved.email
          ? "DATEV-E-Mail-Adresse gespeichert."
          : "DATEV-E-Mail-Adresse geleert – automatischer Versand ist aus.",
      );
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  }

  async function handleDatevBackfill() {
    if (!savedEmail.trim()) {
      setError("Bitte zuerst eine DATEV-E-Mail-Adresse speichern.");
      return;
    }
    if (!backfillFrom) {
      setError("Bitte ein Belegdatum für den Nachzug wählen.");
      return;
    }
    if (
      !window.confirm(
        `Alle noch nicht an DATEV gesendeten Rechnungen ab Belegdatum ${formatDateDe(backfillFrom)} jetzt nachziehen?`,
      )
    ) {
      return;
    }
    setDatevBackfilling(true);
    setError(null);
    try {
      const result = await runDatevBackfill({ fromDate: backfillFrom });
      toast.success(
        `DATEV-Nachzug: ${result.sent} gesendet, ${result.skipped} übersprungen, ${result.failed} fehlgeschlagen (${result.total} geprüft).`,
      );
      const firstError = result.errors?.[0];
      if (result.failed > 0 && firstError) {
        setError(
          `Einige DATEV-Mails sind fehlgeschlagen (z. B. Dokument #${firstError.doc_id}: ${firstError.error}).`,
        );
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "DATEV-Nachzug fehlgeschlagen.");
    } finally {
      setDatevBackfilling(false);
    }
  }

  async function handleBackfill() {
    if (
      !window.confirm(
        "Alle erstellten Geschäftsdokumente ohne SharePoint-Eintrag als PDF in den jeweiligen Projektordner legen?",
      )
    ) {
      return;
    }
    setBackfilling(true);
    setError(null);
    try {
      const result = await runSharepointBackfill({ force: false });
      toast.success(
        `SharePoint-Nachzug: ${result.uploaded} hochgeladen, ${result.skipped} übersprungen, ${result.failed} fehlgeschlagen (${result.total} geprüft).`,
      );
      const firstError = result.errors?.[0];
      if (result.failed > 0 && firstError) {
        setError(
          `Einige Uploads sind fehlgeschlagen (z. B. Dokument #${firstError.doc_id}: ${firstError.error}).`,
        );
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "SharePoint-Nachzug fehlgeschlagen.");
    } finally {
      setBackfilling(false);
    }
  }

  const canDatevBackfill = Boolean(savedEmail.trim()) && Boolean(backfillFrom);

  return (
    <div className="space-y-4">
      <Card
        title="DATEV – Ausgangsrechnungen"
        description="Hinterlegte Adresse erhält automatisch das Rechnungs-PDF (inkl. ZUGFeRD), sobald eine Rechnung den Status „erstellt“ erhält."
        actions={
          <Button type="button" onClick={() => void handleSave()} disabled={saving || loading}>
            Speichern
          </Button>
        }
      >
        {error ? <Alert variant="error">{error}</Alert> : null}
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-label-secondary">
            <Spinner /> Laden …
          </div>
        ) : (
          <div className="max-w-xl space-y-4">
            <div className="space-y-2">
              <TextInput
                label="DATEV-E-Mail-Adresse"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="datev@beispiel.de"
                autoComplete="off"
              />
              <p className="text-xs text-label-secondary">
                Leer lassen, um den automatischen DATEV-Versand zu deaktivieren. Der Versand erfolgt
                ausschließlich über die Absenderadresse no-reply@ernsthaus.de (SMTP) und unterliegt
                dem Safety-Mode.
              </p>
            </div>

            <div className="space-y-2 border-t border-separator/40 pt-4">
              <p className="text-sm font-medium text-label">Vergangene Rechnungen nachziehen</p>
              <p className="text-xs text-label-secondary">
                Sendet alle noch nicht an DATEV übermittelten Rechnungen ab dem gewählten
                Belegdatum (ohne Entwürfe und Stornos).
              </p>
              <TextInput
                label="Nachzug ab Belegdatum"
                type="date"
                value={backfillFrom}
                onChange={(e) => setBackfillFrom(e.target.value)}
                disabled={!savedEmail.trim() || datevBackfilling}
              />
              {!savedEmail.trim() ? (
                <p className="text-xs text-label-secondary">
                  Speichern Sie zuerst eine DATEV-E-Mail-Adresse, um den Nachzug zu starten.
                </p>
              ) : null}
              <Button
                type="button"
                variant="secondary"
                onClick={() => void handleDatevBackfill()}
                disabled={!canDatevBackfill || datevBackfilling || loading}
              >
                {datevBackfilling ? "Nachzug läuft …" : "Nachzug starten"}
              </Button>
            </div>
          </div>
        )}
      </Card>

      <Card
        title="SharePoint-Nachzug"
        description="Lädt PDFs erstellter Geschäftsdokumente nachträglich in den Projektordner auf SharePoint – für Dokumente, die dort noch fehlen."
        actions={
          <Button
            type="button"
            variant="secondary"
            onClick={() => void handleBackfill()}
            disabled={backfilling || loading}
          >
            {backfilling ? "Läuft …" : "Nachzug starten"}
          </Button>
        }
      >
        <p className="text-sm text-label-secondary">
          Betrifft Angebote, Rechnungen, Lieferscheine und Auftragsbestätigungen mit Status ab
          „erstellt“, sofern dem Projekt ein SharePoint-Ordner zugeordnet ist.
        </p>
      </Card>
    </div>
  );
}
